import './PositionDescription.scss';
import React, { useMemo } from 'react';
import { MdNotes } from 'react-icons/md';
import Tooltip from '@srm/ui/tooltip';
import { useIsTruncated } from '../hooks/useIsTruncated';

interface IProps {
    /** Значение реквизита описания позиции. Пустое или из одних пробелов — не рендерим ничего. */
    text?: string | null;
    /**
     * 'icon' — карточка в списке позиций: только значок, текст в бабле;
     * 'line' — шапка позиции: значок и текст одной строкой, бабл только когда текст обрезан.
     */
    variant?: 'icon' | 'line';
}

/**
 * Описание позиции — свободный текст реквизита (какого именно, задаёт настройка
 * шаблона MainDocDescriptionField).
 *
 * Правило одно на оба места: есть текст — есть значок, нет текста — нет разметки.
 * Поэтому на пустом значении возвращаем null, а не пустой span: иначе в мета-ряду
 * шапки остался бы лишний gap, а в карточке — «дырка» справа от имени.
 */
export const PositionDescription: React.FC<IProps> = ({ text, variant = 'icon' }) => {
    const value = text?.trim();
    const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>(value);

    // @atoms/Tooltip прогоняет не-строковый контент через renderToStaticMarkup, чтобы
    // отличить пустую подсказку от непустой. Держим элемент стабильным по ссылке —
    // иначе этот проход повторяется на каждый ре-рендер каждой карточки
    // виртуализованного списка позиций.
    const bubble = useMemo(
        () => (
            <div className="ws-pos-desc__bubble">
                <div className="ws-pos-desc__bubble-hd">
                    <MdNotes size="12px" aria-hidden />
                    Описание позиции
                </div>
                <div className="ws-pos-desc__bubble-body">{value}</div>
            </div>
        ),
        [value],
    );

    if (!value) return null;

    if (variant === 'line') {
        return (
            <Tooltip
                disabled={!isTruncated}
                openDelay={120}
                background="white"
                position="bottom-start"
                maxWidth="380px"
                className="ws-pos-desc-tip ws-pos-desc-tip--line"
                label={bubble}
            >
                <span className="ws-pos-desc ws-pos-desc--line">
                    <MdNotes size="14px" aria-hidden />
                    {/* Текст лежит в DOM целиком, обрезает его только CSS — скринридер
                        читает описание и без бабла. */}
                    <span ref={ref} className="ws-pos-desc__text">
                        {value}
                    </span>
                </span>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            openDelay={120}
            background="white"
            position="bottom-end"
            maxWidth="380px"
            className="ws-pos-desc-tip"
            label={bubble}
        >
            <span className="ws-pos-desc ws-pos-desc--icon" aria-label="Описание позиции">
                <MdNotes size="12px" aria-hidden />
            </span>
        </Tooltip>
    );
};
